import React, { useEffect, useRef } from 'react';
import { RelixHeroSignalNetwork } from '../../components/marketing/RelixHeroSignalNetwork';
import {
  DeliveryAuditSection,
  scrollToDeliveryAudit,
} from '../../components/marketing/solutions/DeliveryAuditForm';
import { MasterVideoSection } from '../../components/marketing/solutions/MasterVideoSection';
import { SignalFlowRow } from '../../components/marketing/solutions/SignalFlowRow';
import { SolutionsStepsRow } from '../../components/marketing/solutions/SolutionsStepsRow';
import { SolutionsFeatureExplorer } from '../../components/marketing/solutions/SolutionsFeatureExplorer';
import { SolutionsFounderSection } from '../../components/marketing/solutions/SolutionsFounderSection';
import { SolutionsIntegrationsStrip } from '../../components/marketing/solutions/SolutionsIntegrationsStrip';
import {
  SolutionsFaqSection,
  SolutionsFooter,
  SolutionsObjectionsSection,
  SolutionsSecuritySection,
  SolutionsTrustBlock,
} from '../../components/marketing/solutions/SolutionsTrustSections';
import { brand } from '../../shared/config/brand';
import { solutionsHero } from '../../shared/config/solutionsContent';
import { marketingSeo } from '../../shared/config/marketingSeo';
import { trackEvent } from '../../shared/analytics';

/**
 * Relix Solutions page.
 *
 * Sells Relix as an executive release decision system, not a dashboard gallery.
 * Hierarchy: Hero (copy + live signals video) → Integrations → Trust block →
 * Master overview video → Signal Layer → Feature Explorer → Objections →
 * Security → FAQ → Free Delivery Audit console CTA → Footer.
 *
 * The audit form posts to the public `POST /api/leads` endpoint (RLX-FEAT-006).
 * Marketing copy is single-sourced in `shared/config/solutionsContent.ts`.
 * Logged-out `/` renders this same page (RLX-FEAT-011); `$pageview` pathname
 * `/` vs `/auth` distinguishes those surfaces — no `marketing_home_view`.
 */

export const SolutionsPage: React.FC = () => {
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = marketingSeo.solutions.title;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const marks = [50, 75];
    const fired = new Set<number>();
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = (window.scrollY / max) * 100;
      for (const mark of marks) {
        if (pct >= mark && !fired.has(mark)) {
          fired.add(mark);
          trackEvent('solutions_scroll', { depth: mark });
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash === 'audit-form' || hash === 'delivery-audit') {
      scrollToDeliveryAudit(emailInputRef.current);
    }
  }, []);

  const goToAudit = () => scrollToDeliveryAudit(emailInputRef.current);

  const scrollToExplorer = () => {
    document
      .getElementById('solutions-feature-explorer')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="solutions-page solutions-page--minimal solutions-page--relix">
      <section
        className="hero-section solutions-hero solutions-hero--split solutions-hero--video animate-fade-in"
        aria-label="Relix — know what will ship before it slips"
      >
        <div className="solutions-hero-bg" aria-hidden="true">
          <video
            className="solutions-hero-bg-video"
            src="/brand/solutions/hero-loop.mp4?v=2"
            poster="/brand/solutions/hero-loop-poster.jpg?v=2"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            tabIndex={-1}
          />
          <div className="solutions-hero-bg-veil" />
        </div>
        <div className="solutions-hero-signal-network-layer" aria-hidden="true">
          <RelixHeroSignalNetwork />
        </div>
        <div className="solutions-hero-split solutions-hero-grid">
          <div className="solutions-hero-copy">
            <div
              className="solutions-hero-brand animate-slide-up"
              style={{ animationDelay: '0.02s' }}
            >
              <span className="solutions-hero-eyebrow solutions-hero-eyebrow--violet">
                {solutionsHero.eyebrow}
              </span>
            </div>
            <h1
              className="hero-main-product-headline solutions-hero-title animate-slide-up"
              style={{ animationDelay: '0.06s' }}
            >
              Know what will ship before <span className="solutions-hero-accent">it slips.</span>
            </h1>
            <p
              className="solutions-hero-line solutions-hero-line--supporting solutions-hero-supporting animate-slide-up"
              style={{ animationDelay: '0.1s' }}
            >
              {solutionsHero.supporting}
            </p>
            <div
              className="solutions-hero-actions hero-buttons solutions-hero-ctas animate-slide-up"
              style={{ animationDelay: '0.16s' }}
            >
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  trackEvent('solutions_hero_cta', { cta: 'primary' });
                  goToAudit();
                }}
              >
                {brand.primaryCta}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  trackEvent('solutions_hero_cta', { cta: 'secondary' });
                  scrollToExplorer();
                }}
              >
                {solutionsHero.secondaryCta}
              </button>
            </div>
          </div>
        </div>
      </section>

      <SolutionsIntegrationsStrip />

      <SolutionsTrustBlock />

      <MasterVideoSection />

      <SolutionsFounderSection onAuditClick={goToAudit} />

      <section
        id="solutions-signal-flow"
        className="solutions-signal-layer"
        aria-labelledby="solutions-signal-layer-heading"
      >
        <header className="solutions-section-header">
          <span className="solutions-section-eyebrow">ONE DELIVERY ROUTE</span>
          <h2 id="solutions-signal-layer-heading">Every signal connected.</h2>
          <p className="solutions-section-sub">
            From early forecast to final delivery review, Relix connects the signals that matter so
            you can act with confidence.
          </p>
        </header>
        <SolutionsStepsRow />
        <SignalFlowRow />
      </section>

      <SolutionsFeatureExplorer onAuditClick={goToAudit} lazyMockups />

      <SolutionsObjectionsSection />

      <SolutionsSecuritySection variant="strip" />

      <SolutionsFaqSection />

      <DeliveryAuditSection surface="solutions" emailInputRef={emailInputRef} />

      <SolutionsFooter />
    </div>
  );
};
